/** Full-page TGS Zelle receipt HTML + PNG helpers. */
import { KNOWN_RECEIPT_FOOTER, transferSkipsId } from "@/utils/knownClient";

function esc(s) {
  return String(s ?? "").replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));
}

export const RECEIPT_FOOTER =
  "Moun k ap resevwa Zelle la dwe konnen nimewo transfè a epi mache ak yon pyès idantite pou li rekipere kòb la nan biwo a.";

export function receiptFooter(tr) {
  return transferSkipsId(tr) ? KNOWN_RECEIPT_FOOTER : RECEIPT_FOOTER;
}

export function buildReceiptRows(tr, fmtMoney, fmtDate) {
  const total = tr.total_amount != null ? tr.total_amount : (Number(tr.amount || 0) + Number(tr.service_fee || 0));
  return [
    ["No# Transfè", tr.transfer_id || "—"],
    ["Moun ki voye Zelle la", tr.sender_name || "—"],
    ["WhatsApp kliyan", tr.sender_whatsapp || "—"],
    ["Moun k ap resevwa a", tr.client_name || "—"],
    ["WhatsApp benefisyè", tr.client_whatsapp || tr.client_phone || "—"],
    ["Kote pou pase pran lajan an", tr.client_address || "—"],
    // NOTE: never include the delivering agent's name on the receipt —
    // receipts are shared with the client, who must not know which agent
    // delivers their money. Agent identity stays admin-only.
    ["Montan li ap jwenn", fmtMoney(tr.amount)],
    ["Frè sèvis", fmtMoney(tr.service_fee || 0)],
    ["Montan total", fmtMoney(total)],
    ["Dat kreyasyon", fmtDate(tr.created_at)],
    tr.delivered_at ? ["Dat livrezon", fmtDate(tr.delivered_at)] : null,
  ].filter(Boolean);
}

/** Full A4 receipt HTML — fills the print page edge-to-edge. */
export function buildReceiptHtml(tr, { fmtMoney, fmtDate, origin = window.location.origin, autoPrint = false } = {}) {
  const rows = buildReceiptRows(tr, fmtMoney, fmtDate);
  return `<!doctype html><html><head><meta charset="utf-8"/><meta name="viewport" content="width=device-width,initial-scale=1"/>
<title>Resi ${esc(tr.transfer_id)}</title>
<style>
  @page { size: A4 portrait; margin: 0; }
  * { box-sizing: border-box; }
  html, body { width: 100%; height: 100%; margin: 0; padding: 0; }
  body {
    font-family: 'Manrope', Arial, sans-serif;
    color: #0f172a;
    background: #fff;
    width: 210mm;
    min-height: 297mm;
    height: 297mm;
    padding: 12mm 14mm;
    position: relative;
    display: flex;
    flex-direction: column;
  }
  .wm {
    position: absolute; inset: 0; display: flex; align-items: center; justify-content: center;
    z-index: 0; pointer-events: none;
  }
  .wm span {
    transform: rotate(-32deg); white-space: nowrap;
    font-size: 48mm; font-weight: 800; letter-spacing: .12em;
    text-transform: uppercase; color: rgba(217,119,6,.22);
  }
  .sheet {
    position: relative; z-index: 1; flex: 1;
    display: flex; flex-direction: column;
    width: 100%; height: 100%;
  }
  .head {
    text-align: center;
    border-bottom: 3px solid #1e40af;
    padding-bottom: 6mm;
    margin-bottom: 5mm;
  }
  .logo { width: 32mm; height: 32mm; object-fit: contain; margin: 0 auto 3mm; display: block; }
  .sub { color: #1e293b; font-size: 3mm; letter-spacing: .25em; text-transform: uppercase; font-weight: 700; }
  h1 { font-size: 7mm; margin: 2mm 0 0; font-weight: 800; line-height: 1.15; }
  .rows {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: space-evenly;
  }
  .row {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 6mm;
    padding: 2.2mm 0;
    border-bottom: 1px solid #64748b;
    font-size: 3.8mm;
  }
  .row .k { color: #1e293b; font-weight: 700; }
  .row .v { font-weight: 800; text-align: right; word-break: break-word; max-width: 55%; }
  .foot {
    margin-top: 5mm;
    text-align: center;
    color: #0f172a;
    font-size: 3.4mm;
    font-weight: 700;
    line-height: 1.45;
    padding: 4mm 3mm;
    border: 2px solid #1e40af;
    border-radius: 2mm;
    background: #dbeafe;
  }
  @media print {
    html, body { width: 210mm; height: 297mm; }
    body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    .wm span, .foot { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  }
  @media screen {
    body {
      margin: 0 auto;
      box-shadow: 0 0 24px rgba(15,23,42,.12);
    }
  }
</style></head><body>
  <div class="wm"><span>PAID</span></div>
  <div class="sheet">
    <div class="head">
      <img class="logo" src="${esc(origin)}/tgs-logo.png" alt="TGS"/>
      <div class="sub">Takara Global Services</div>
      <h1>Resi Transfè Zelle</h1>
    </div>
    <div class="rows">
      ${rows.map((r) => `<div class="row"><span class="k">${esc(r[0])}</span><span class="v">${esc(r[1])}</span></div>`).join("")}
    </div>
    <div class="foot">${esc(receiptFooter(tr))}</div>
  </div>
  ${autoPrint ? `<script>window.onload=function(){setTimeout(function(){window.print();},300);}</script>` : ""}
</body></html>`;
}

function wrapText(ctx, text, maxWidth) {
  const words = String(text).split(/\s+/);
  const lines = [];
  let line = "";
  words.forEach((w) => {
    const test = line ? `${line} ${w}` : w;
    if (ctx.measureText(test).width > maxWidth && line) {
      lines.push(line);
      line = w;
    } else {
      line = test;
    }
  });
  if (line) lines.push(line);
  return lines;
}

/** Draw a full A4-proportion receipt onto canvas and return a PNG Blob. */
export async function receiptPngBlob(tr, { fmtMoney, fmtDate } = {}) {
  const rows = buildReceiptRows(tr, fmtMoney, fmtDate);
  const W = 794;
  const H = 1123;
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");

  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, W, H);

  ctx.save();
  ctx.translate(W / 2, H / 2);
  ctx.rotate((-32 * Math.PI) / 180);
  ctx.fillStyle = "rgba(217,119,6,0.22)";
  ctx.font = "800 120px Arial";
  ctx.textAlign = "center";
  ctx.fillText("PAID", 0, 0);
  ctx.restore();

  try {
    const img = await loadImage(`${window.location.origin}/tgs-logo.png`);
    ctx.drawImage(img, (W - 110) / 2, 36, 110, 110);
  } catch {
    /* logo optional */
  }

  ctx.fillStyle = "#1e293b";
  ctx.font = "700 13px Arial";
  ctx.textAlign = "center";
  ctx.fillText("TAKARA GLOBAL SERVICES", W / 2, 168);

  ctx.fillStyle = "#0f172a";
  ctx.font = "800 28px Arial";
  ctx.fillText("Resi Transfè Zelle", W / 2, 205);

  ctx.strokeStyle = "#1e40af";
  ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.moveTo(48, 225);
  ctx.lineTo(W - 48, 225);
  ctx.stroke();

  const top = 255;
  const bottom = H - 160;
  const step = rows.length > 1 ? (bottom - top) / (rows.length - 1) : 0;
  rows.forEach(([k, v], i) => {
    const y = top + i * step;
    ctx.fillStyle = "#1e293b";
    ctx.font = "700 16px Arial";
    ctx.textAlign = "left";
    ctx.fillText(String(k), 56, y);
    ctx.fillStyle = "#0f172a";
    ctx.font = "800 16px Arial";
    ctx.textAlign = "right";
    const val = String(v);
    if (ctx.measureText(val).width > 340) {
      ctx.font = "700 13px Arial";
    }
    ctx.fillText(val.length > 42 ? `${val.slice(0, 40)}…` : val, W - 56, y);
    ctx.strokeStyle = "#64748b";
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(56, y + 12);
    ctx.lineTo(W - 56, y + 12);
    ctx.stroke();
  });

  // Footer box
  ctx.fillStyle = "#dbeafe";
  ctx.strokeStyle = "#1e40af";
  ctx.lineWidth = 2;
  ctx.fillRect(48, H - 130, W - 96, 95);
  ctx.strokeRect(48, H - 130, W - 96, 95);
  ctx.fillStyle = "#0f172a";
  ctx.font = "700 14px Arial";
  ctx.textAlign = "center";
  const lines = wrapText(ctx, receiptFooter(tr), W - 130);
  lines.forEach((ln, i) => {
    ctx.fillText(ln, W / 2, H - 100 + i * 20);
  });

  return await new Promise((resolve) => canvas.toBlob((b) => resolve(b), "image/png"));
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

export function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}
